import { RaasPortalId } from "../../types";
import { constants, Icon, Loader, Text } from "@findemdev/maui";
import { useMemo, useState } from "react";
import type { IGetCompanyLogosResponseDataObj } from "../../../../types/pub";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  LoadingState,
  TableSection,
  TableViewport,
} from "../../RaasClientPortal.styled";
import type { ISortConfig, IProjectRow, ProjectTableColumnKey, SortableColumnKey, SortOrder } from "../../types";
import { ProjectsPagination } from "./ProjectsPagination";
import { ProjectsTable } from "./ProjectsTable";
import { useVisibleProjectRows } from "./useVisibleProjectRows";

const { colors } = constants;

interface IProjectTableSectionProps {
  portalId?: RaasPortalId;
  canGoNext?: boolean;
  columnKeys: readonly ProjectTableColumnKey[];
  companyDetailsByMetaRef: Record<
    string,
    IGetCompanyLogosResponseDataObj | undefined
  >;
  filteredRowsCount: number;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  onDeleteProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onEditProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onNextPage: () => void;
  onPageClick: ({ pageNumber }: { pageNumber: number }) => void;
  onPreviousPage: () => void;
  onRowClick: ({ project }: { project: IProjectRow["project"] }) => void;
  onRowKeyDown: (
    args: {
      event: React.KeyboardEvent<HTMLElement>;
      project: IProjectRow["project"];
    },
  ) => void;
  isRowClickable: (row: IProjectRow) => boolean;
  onSort: (field: SortableColumnKey, order: SortOrder | null) => void;
  onVisibleProjectRefsChange: ({ projectRefs }: { projectRefs: string[] }) => void;
  paginatedRows: IProjectRow[];
  sortConfig: ISortConfig | null;
  rowsCount: number;
  safeCurrentPage: number;
  totalPages: number;
  trackVisibleRows: boolean;
  visiblePages: number[];
}

export const ProjectTableSection = ({
  canGoNext,
  columnKeys,
  portalId,
  companyDetailsByMetaRef,
  filteredRowsCount,
  isError,
  isFetching,
  isLoading,
  isRowClickable,
  onDeleteProject,
  onEditProject,
  onNextPage,
  onPageClick,
  onPreviousPage,
  onRowClick,
  onRowKeyDown,
  onSort,
  onVisibleProjectRefsChange,
  paginatedRows,
  rowsCount,
  safeCurrentPage,
  sortConfig,
  totalPages,
  trackVisibleRows,
  visiblePages,
}: IProjectTableSectionProps) => {
  const [tableViewport, setTableViewport] = useState<HTMLDivElement | null>(null);
  const projectRefs = useMemo(
    () => paginatedRows.map((row) => row.project.ref),
    [paginatedRows],
  );
  useVisibleProjectRows({
    enabled: trackVisibleRows,
    onVisibleProjectRefsChange,
    projectRefs,
    viewport: tableViewport,
  });
  const isLoadedPageFilteredEmpty =
    trackVisibleRows && rowsCount > 0 && filteredRowsCount === 0;

  if (isLoading || (isFetching && rowsCount === 0)) {
    return (
      <TableSection>
        <LoadingState>
          <Loader />
          <Text as="span">Loading projects</Text>
        </LoadingState>
      </TableSection>
    );
  }

  if (isError) {
    return (
      <TableSection>
        <EmptyState>
          <Icon icon="error_outline" $size={36} $color={colors["red-300"]} />
          <EmptyStateTitle>Projects could not be loaded</EmptyStateTitle>
          <EmptyStateDescription>
            Refresh the page or try again in a moment.
          </EmptyStateDescription>
        </EmptyState>
      </TableSection>
    );
  }

  if (filteredRowsCount === 0) {
    return (
      <TableSection>
        <EmptyState>
          <Icon icon="folder_open" $size={42} $color={colors["grey-400"]} />
          <EmptyStateTitle>
            {isLoadedPageFilteredEmpty
              ? "No matches on this loaded page"
              : "No projects found"}
          </EmptyStateTitle>
          <EmptyStateDescription>
            {isLoadedPageFilteredEmpty
              ? "Try another page or adjust the search and filters."
              : "Adjust the search or filters to see results."}
          </EmptyStateDescription>
        </EmptyState>

        {isLoadedPageFilteredEmpty ? (
          <ProjectsPagination
            canGoNext={canGoNext}
            onNextPage={onNextPage}
            onPageClick={onPageClick}
            onPreviousPage={onPreviousPage}
            safeCurrentPage={safeCurrentPage}
            totalPages={totalPages}
            visiblePages={visiblePages}
          />
        ) : null}
      </TableSection>
    );
  }

  return (
    <TableSection>
      <TableViewport ref={setTableViewport}>
        <ProjectsTable
          portalId={portalId}
          columnKeys={columnKeys}
          companyDetailsByMetaRef={companyDetailsByMetaRef}
          isRowClickable={isRowClickable}
          onDeleteProject={onDeleteProject}
          onEditProject={onEditProject}
          onRowClick={onRowClick}
          onRowKeyDown={onRowKeyDown}
          onSort={onSort}
          rows={paginatedRows}
          sortConfig={sortConfig}
        />
      </TableViewport>

      <ProjectsPagination
        canGoNext={canGoNext}
        onNextPage={onNextPage}
        onPageClick={onPageClick}
        onPreviousPage={onPreviousPage}
        safeCurrentPage={safeCurrentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
      />
    </TableSection>
  );
};
